import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { map } from 'rxjs/operators';

import {Settings, UserProfile} from '../model/model';
import {UserRepository} from '../repositories/user.repository';

@Injectable()
export class UserService {

  // cache new badge count
  private _newBadgeCount: number = 0;

  // cache new content count
  private _newContentCount: number = 0;

  // cache admin mode
  private _adminMode: boolean = false;

  // cache devContentImmediately
  private _devContentImmediately: boolean = false;

  // cache devEpisodes (MONDAY, FRIDAY, '')
  private _devEpisodes: string = '';

  // cache theme
  private _theme: string = '';

  // cache mode
  private _mode: string = '';

  private _cachedProfile: UserProfile;

  constructor(private http: HttpClient, private userRepository: UserRepository) { }

  public setTimeInfo(utcOffset: number, lang: string): Observable<boolean> {
    return this.userRepository.setTimeInfo(utcOffset, lang);
  }

  public setDeviceInfo(deviceToken: string, deviceType: string, deviceName: string, appVersion: string): Observable<boolean> {
    return this.userRepository.setDeviceInfo(deviceToken, deviceType, deviceName, appVersion);
  }

  public getUserProfile(type: string): Observable<UserProfile> {
    if (type == 'full') {
      return this.userRepository.getUserProfile(type).pipe(map((profile: UserProfile) => {
        this._cachedProfile = profile;
        return profile;
      }));
    }
    return this.userRepository.getUserProfile(type).pipe(map((profile: UserProfile) => {
        if (this._cachedProfile === undefined && profile.company !== undefined) {
          this._cachedProfile = profile;
        }
        return profile;
      }));
  }

  public getTargetGroups(): Observable<Array<any>> {
    return this.userRepository.getTargetGroups();
  }

  public markBadgeDisplayed(userbadgeId: string): Observable<boolean> {
    return this.userRepository.markBadgesDisplayed([userbadgeId]);
  }

  public setSettings(settings: Settings): Observable<boolean> {
    return this.userRepository.setSettings(settings);
  }

  public markContentsDisplayed(userContentIds: Array<string>): Observable<boolean> {
    return this.userRepository.markContentsDisplayed(userContentIds);
  }

  public getLeader(leaderId: string): Observable<string> {
    return this.userRepository.getLeader(leaderId);
  }

  public setLeader(leaderId: string): Observable<boolean> {
    return this.userRepository.setLeader(leaderId);
  }

  public markLaunchReviewDone(): Observable<boolean> {
    return this.userRepository.markLaunchReviewDone();
  }

  // static helper functions
  public setNewBadgeCount(badgeCount: number): void {
    this._newBadgeCount = badgeCount;
  }

  public getNewBadgeCount(): number {
    return this._newBadgeCount;
  }

  public setNewContentCount(contentCount: number): void {
    this._newContentCount = contentCount;
  }

  public getNewContentCount(): number {
    return this._newContentCount;
  }

  public setAdminMode(adminMode: boolean): void {
    this._adminMode = adminMode;
  }

  public getAdminMode(): boolean {
    return this._adminMode;
  }

  public setMode(mode: string): void {
    this._mode = mode;
  }

  public getMode(): string {
    return this._mode;
  }

  public setDevContentImmediately(devContentImmediately: boolean): void {
    this._devContentImmediately = devContentImmediately;
  }

  public getDevContentImmediately(): boolean {
    return this._devContentImmediately;
  }

  public setDevEpisodes(devEpisodes: string): void {
    this._devEpisodes = devEpisodes;
  }

  public getDevEpisodes(): string {
    return this._devEpisodes;
  }

  public getCompanyThemes(profile: UserProfile): Array<string> {
    // test
    if ( this._adminMode ) {
      return ['lhg', 'fabw', 'swb', 'ergo', 'ens', 'dreso', 'hambg', 'airst', 'wisag', 'lbank', 'conac', 'lbbw', 'synk', 'non', 'ssb'];
    }
    const themes: Array<string> = [];
    const lowName = profile.company.name.toLowerCase();
    if (profile.company.name.indexOf('Lufthansa') !== -1) {
      themes.push('lhg');
    }
    // if (profile.company.name.indexOf('Bertelsmann') !== -1) {
    //   themes.push('bst');
    // }
    // if (profile.company.name.indexOf('Deka') !== -1) {
    //   themes.push('deka');
    // }
    if (profile.company.name.toLowerCase().indexOf('fa_bw') !== -1 || profile.company.name.toLowerCase().indexOf('fa_hlp') !== -1) {
      themes.push('fabw');
    }
    // if (profile.company.name.indexOf('AFS') !== -1) {
    //   themes.push('afs');
    // }
    if (profile.company.name.indexOf('SWB') !== -1) {
      themes.push('swb');
    }
    // if (profile.company.name.indexOf('Consileon') !== -1 || profile.email.indexOf('consileon') !== -1) {
    //   themes.push('cb');
    // }
    if (profile.company.name.indexOf('ERGO') !== -1 || lowName.indexOf('ergo') === 0) {
      themes.push('ergo');
    }
    if (profile.company.name.indexOf('LB') === 0 || lowName.indexOf('lbbw') !== -1) {
      themes.push('lbbw');
    }
    // if (profile.company.name.indexOf('FC B') === 0) {
    //   themes.push('fcb');
    // }
    if (lowName.indexOf('energie') !== -1 && lowName.indexOf('schwaben') !== -1 ) {
      themes.push('ens');
    }
    if (lowName.indexOf('dreso') === 0 || lowName.indexOf('neext') !== -1) {
      themes.push('dreso');
    }

    if (lowName.indexOf('hamburg') === 0) {
      themes.push('hambg');
    }

    if (lowName.indexOf('airstuttgart') === 0) {
      themes.push('airst');
    }
    
    if (lowName.indexOf('wisag') === 0) {
      themes.push('wisag');
    }

    if (lowName.indexOf('l-bank') === 0) {
      themes.push('lbank');
    }
    if (lowName.indexOf('consileon') !== -1 && lowName.indexOf('academy') !== -1) {
      themes.push('conac');
    }
    if (profile.company.name.indexOf('SYNK') !== -1 || profile.email.indexOf('@synk-group.com') !== -1) {
      themes.push('synk');
    }
    if (lowName.indexOf('nonnenhof') !== -1) {
      themes.push('non');
    }
    if (profile.company.name.indexOf('SSB') === 0) {
      themes.push('ssb');
    }

    return themes;
  }

  public getCreated(): Observable<Date> {
    if ( this._cachedProfile !== undefined ) {
      return of(this._cachedProfile.createdAt)
    }
    return this.getUserProfile('full').pipe(map((profile: UserProfile) => {
      this._cachedProfile = profile;
      return this._cachedProfile.createdAt;
    }));
  }

  public getCompanyCached(): string {
    return this._cachedProfile?.company?.name;
  }

  public getCompanyEpisodesCached(): number {
    return this._cachedProfile?.company?.episodes;
  }

  public isKontrollGroupCached(): boolean {
    let res = false;
    if (this._cachedProfile !== undefined && this._cachedProfile.groups != undefined) {
      return this._cachedProfile.groups.find(group => group.name.indexOf('Kontroll') == 0) !== undefined
    }
    return false;
  }
}
